import { useSyncExternalStore } from 'react';

/**
 * El corte entre móvil y escritorio, en un solo sitio.
 *
 * Casi todo el diseño responsive se resuelve con clases `lg:` y no necesita
 * saber esto. Pero hay decisiones que no son de pintado sino de comportamiento
 * —en escritorio, pulsar un gasto abre el panel derecho en vez de navegar— y
 * ésas sí hay que tomarlas en JavaScript.
 *
 * El valor tiene que ser **el mismo** que el `lg` de Tailwind (1024px). Si se
 * separan, aparece la peor de las incoherencias: el ancho en el que la interfaz
 * se pinta como escritorio pero se comporta como móvil, o al revés.
 */
export const ANCHO_ESCRITORIO = 1024;

/**
 * ¿Estamos en escritorio **ahora mismo**?
 *
 * Sólo para manejadores de eventos, nunca durante el render. En el render se
 * usan clases `lg:`: la aplicación arranca de la caché del service worker y
 * decidir la maqueta en JavaScript provoca un salto en el primer pintado, con
 * la barra inferior apareciendo y desapareciendo. En un `onClick` ese problema
 * no existe, porque para entonces ya hay ventana y ya está pintada.
 */
export function esEscritorio(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(`(min-width: ${ANCHO_ESCRITORIO}px)`).matches;
}

function suscribirAlAncho(avisar: () => void): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const consulta = window.matchMedia(`(min-width: ${ANCHO_ESCRITORIO}px)`);
  consulta.addEventListener('change', avisar);
  return () => consulta.removeEventListener('change', avisar);
}

/**
 * Lo mismo, pero reaccionando al cambio de tamaño de la ventana.
 *
 * La prohibición de arriba —no decidir en el render— es sobre **la maqueta**:
 * lo que se pinta se decide con clases `lg:`, y por eso la lateral y la barra
 * inferior coexisten en el árbol (ver `AppShell`). Esto es para lo otro: decidir
 * si una consulta **se lanza**.
 *
 * Hace falta justo por esa coexistencia. La lateral está montada también en un
 * teléfono, sólo que oculta, así que un `useQuery` suyo se ejecuta igual en
 * móvil. Con una consulta barata da igual; con `my_home_summary`, que recorre
 * noventa días de eventos, sería pagar en cada pantalla del móvil por un menú
 * que ahí no existe.
 */
export function useEsEscritorio(): boolean {
  return useSyncExternalStore(suscribirAlAncho, esEscritorio, () => false);
}
